// The re-envisioned shell (docs/wchdash-spec/06 §5): ONE nav row, no sidebar.
// Grouped by job, not data source — Company Pulse, Listing Health, Money ▾, Reviews,
// Reports, Impact, then the right cluster (month, search, refresh, settings ▾,
// sign out). Active page = the gray-900 pill. Dropdown pages keep their parent
// highlighted. ~44px of chrome total; everything else is the work.

import { notifBellHtml, bindNotifBell, mountNotifBell } from './NotifBell';   // R115 C1: task notification bell

interface NavLink { id: string; label: string }
interface NavItem { id: string; label: string; children?: NavLink[] }

const NAV: NavItem[] = [
  { id: 'triage', label: 'Priority Queue' },
  {
    id: 'money', label: 'Money', children: [
      { id: 'money', label: 'Overview · pace & projections' },
      { id: 'reservations', label: 'Reservations' },
    ],
  },
  { id: 'reviews', label: 'Reviews' },
  { id: 'reports', label: 'Reports' },
  { id: 'impact', label: 'Impact' },
  {
    id: 'onboarding', label: 'Ops', children: [
      { id: 'onboarding', label: 'Onboarding & property details' },
      { id: 'statements', label: 'Owner statement review' },
      { id: 'mtr', label: 'MTR · vacancies, rates, leads' },
    ],
  },
  {
    id: 'maintenance', label: 'Asset Ops', children: [
      { id: 'issues', label: 'Issues · all-in-one board' },
      { id: 'maintenance', label: 'Maintenance · WOs & PMs' },
      { id: 'inspections', label: 'Inspections' },
      { id: 'housekeeping', label: 'Housekeeping' },
      { id: 'supplies', label: 'Supplies & inventory' },
    ],
  },
];

const SETTINGS_MENU: NavLink[] = [
  { id: 'admin', label: 'Admin · properties, owners, formulas' },
  { id: 'glossary', label: 'Metric glossary' },
];

// R78 (Jesse): the account menu's Admin/glossary entries are office-only —
// housekeepers and other field staff were seeing them. Same whitelist as the
// Sidebar's Settings bucket and the owner_handled override.
const OFFICE_ROLES = ['admin', 'director', 'manager', 'remote_ops'];

// Pages reached from inside another page keep that page's pill lit.
const ACTIVE_ALIAS: Record<string, string> = {
  'data-explorer': 'triage',
  'listings': 'triage',
  'reservations': 'money',
  'portfolio': 'money',
  'projections': 'money',
  'profitability': 'money',
  'statements': 'onboarding',
  'mtr': 'onboarding',
  'issues': 'maintenance',
  'inspections': 'maintenance',
  'housekeeping': 'maintenance',
  'supplies': 'maintenance',
  'owners': 'admin',
  'settings': 'admin',
  'operations': 'admin',
  'booking-quote': 'admin',
};

export class TopNav {
  private currentActive = 'today';
  private onNavigate: (page: string) => void;
  private userLoaded = false;
  private cachedName = '';
  private cachedInitials = '··';

  constructor(onNavigate: (page: string) => void) {
    this.onNavigate = onNavigate;
  }

  setActive(page: string): void {
    this.currentActive = ACTIVE_ALIAS[page] || page;
  }

  private inSettings(): boolean {
    return SETTINGS_MENU.some(s => s.id === this.currentActive);
  }

  render(): string {
    const link = (item: NavItem) => {
      const active = this.currentActive === item.id;
      if (!item.children) {
        return `<button class="tn-link ${active ? 'active' : ''}" data-nav="${item.id}">${item.label}</button>`;
      }
      return `
        <div class="tn-drop">
          <button class="tn-link ${active ? 'active' : ''}" data-nav="${item.id}">${item.label} <span style="font-size:9px; opacity:0.7;">▾</span></button>
          <div class="tn-menu">
            ${item.children.map(c => `<button class="tn-menu-item" data-nav="${c.id}">${c.label}</button>`).join('')}
          </div>
        </div>`;
    };

    const currentMonth = new Date().toISOString().slice(0, 7);
    // Effective role (View-as aware — a director viewing as cleaner should see
    // the cleaner's menu). Default 'admin' matches the Sidebar's no-session dev
    // fallback.
    let role = 'admin';
    try { role = sessionStorage.getItem('userRole') || 'admin'; } catch (_) { /* */ }
    const settingsItems = OFFICE_ROLES.includes(role) ? SETTINGS_MENU : [];
    const roleLabel = role.replace(/_/g, ' ');
    return `
      <nav class="topnav">
        <button class="tn-icon tn-hamburger" id="sidebar-toggle" title="Menu" aria-label="Menu">☰</button>
        <button class="tn-search" id="search-btn" title="Search (⌘K)">
          <span>🔍</span><span class="tn-search-ph">Search listings, reservations, tickets…</span><kbd>⌘K</kbd>
        </button>
        <span style="flex:1;"></span>
        <input type="month" id="global-month" value="${currentMonth}" class="tn-month" title="Reporting month">
        <span id="last-updated" class="tn-updated" title="Click refresh to update">just now</span>
        <button class="tn-icon" id="refresh-btn" title="Refresh data">↻</button>
        ${notifBellHtml()}
        <div class="tn-drop tn-drop-right">
          <button class="tn-user" id="tn-settings" title="Account">
            <span class="tn-avatar" id="tn-avatar">··</span>
            <span class="tn-user-meta"><span class="tn-user-name" id="tn-user-name">Account</span><span class="tn-user-role" style="text-transform:capitalize;">${roleLabel}</span></span>
            <span style="font-size:9px; opacity:0.6;">▾</span>
          </button>
          <div class="tn-menu">
            ${settingsItems.map(c => `<button class="tn-menu-item" data-nav="${c.id}">${c.label}</button>`).join('')}
            ${settingsItems.length ? '<div style="border-top:1px solid var(--border-soft); margin:4px 0;"></div>' : ''}
            <button class="tn-menu-item" id="tn-signout">Sign out</button>
          </div>
        </div>
      </nav>`;
  }

  // R115 C1: App starts the bell's 60 s poll after the first page load.
  mountBell(): void { mountNotifBell(); }

  bindEvents(): void {
    bindNotifBell();   // R115 C1: the freshly rendered bell (badge/menu drawn from its cache)
    // Mobile: toggle the off-canvas sidebar.
    document.getElementById('sidebar-toggle')?.addEventListener('click', () => {
      document.body.classList.toggle('sidebar-mobile-open');
    });
    // Fill the user chip (avatar initials + name) from the Supabase session.
    if (!this.userLoaded) {
      this.userLoaded = true;
      import('../../lib/supabase').then(({ supabase }) => supabase.auth.getUser()).then(({ data }: any) => {
        const email = data?.user?.email || '';
        if (email) {
          const name = email.split('@')[0];
          const initials = (name.replace(/[^a-zA-Z]/g, '').slice(0, 2) || email.slice(0, 2)).toUpperCase();
          this.cachedName = name; this.cachedInitials = initials;
          const av = document.getElementById('tn-avatar'); if (av) av.textContent = initials;
          const nm = document.getElementById('tn-user-name'); if (nm) nm.textContent = name;
        }
      }).catch(() => {});
    } else if (this.cachedName) {
      const av = document.getElementById('tn-avatar'); if (av) av.textContent = this.cachedInitials;
      const nm = document.getElementById('tn-user-name'); if (nm) nm.textContent = this.cachedName;
    }
    document.querySelectorAll<HTMLElement>('.topnav [data-nav]').forEach(el => {
      el.addEventListener('click', () => {
        const page = el.dataset.nav!;
        // close any open dropdown
        document.querySelectorAll('.tn-drop.open').forEach(d => d.classList.remove('open'));
        this.onNavigate(page);
      });
    });
    // Dropdowns open on click (touch-friendly), close on outside click.
    document.querySelectorAll<HTMLElement>('.topnav .tn-drop').forEach(drop => {
      const trigger = drop.querySelector<HTMLElement>('.tn-link, #tn-settings');
      trigger?.addEventListener('click', (e) => {
        // a parent with children both navigates AND opens; toggle the menu
        e.stopPropagation();
        const wasOpen = drop.classList.contains('open');
        document.querySelectorAll('.tn-drop.open').forEach(d => d.classList.remove('open'));
        if (!wasOpen) drop.classList.add('open');
      });
    });
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.tn-drop')) {
        document.querySelectorAll('.tn-drop.open').forEach(d => d.classList.remove('open'));
      }
    });
    document.getElementById('tn-signout')?.addEventListener('click', async () => {
      try {
        const { supabase } = await import('../../lib/supabase');
        await supabase.auth.signOut();
      } catch (_) { /* fall through */ }
      window.location.href = '/login';
    });
  }
}
